import { IonButton, IonIcon, useIonAlert } from '@ionic/react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { sendSharp, shareSocialOutline } from 'ionicons/icons';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useDeleteMoneyCollection } from '../../api/hooks/moneyCollection/useDeleteMoneyCollection';
import { useMoneyCollection } from '../../api/hooks/moneyCollection/useMoneyCollection';
import { useMutateMoneyCollectionPublicShare } from '../../api/hooks/moneyCollection/useMutateMoneyCollectionPublicShare';
import { useSendMoneyCollectionEmail } from '../../api/hooks/moneyCollection/useSendMoneyCollectionEmail';
import { useCompanyLogo } from '../../api/hooks/useCompanyLogo';
import { EmailModal, pdfStyles } from '../../components';
import { EmailPill } from '../../components/atoms/EmailPill';
import { Loading } from '../../components/atoms/Loading';
import { ViewField } from '../../components/atoms/ViewField';
import { ViewTextAreaField } from '../../components/atoms/ViewTextAreaField';
import { MoneyCollectionPdf } from '../../components/molecules/pdf/moneyCollection/MoneyCollectionPdf';
import { ENV } from '../../config/env';
import { getLocalDate, isAfter24Hours } from '../../config/utils/dateHelpers';
import { CURRENCY, SUBSCRIPTION } from '../../interfaces/enumerables';
import { TRADER_TYPE } from '../../interfaces/moneyCollection.interface';
import { PATHS } from '../../navigation/data';
import { useHasActivePlan } from '../../services/utils/useHasActivePlan';
import { setToast } from '../../store/core/coreSlice';
import { useAppDispatch } from '../../store/hooks';

export const MoneyCollectionScreen = () => {
  useHasActivePlan(true, SUBSCRIPTION.SUBTOTALS);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const [presentAlert] = useIonAlert();
  const { data, isLoading } = useMoneyCollection(id);
  const { data: logoData } = useCompanyLogo(data?.company?.logoUrl || '');
  const { mutate: deleteEntity, isLoading: isLoadingDelete } = useDeleteMoneyCollection(navigate);
  const { isLoading: isLoadingSendEmail, mutate: sendEmail } = useSendMoneyCollectionEmail();
  const { mutate: publicShareDoc } = useMutateMoneyCollectionPublicShare();

  // TODO: populate currency of customer, for now it's fine because we only have euros
  const currency = useMemo(() => data?.customer?.currency?.currencyCode || 'EUR', [data?.customer?.currency]);
  const currencySymbol = useMemo(() => CURRENCY?.[currency] || CURRENCY.EUR, [currency]);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const openSendEmail = useCallback(() => {
    setShowEmailModal(true);
  }, []);
  const handleCancel = useCallback(() => {
    setShowEmailModal(false);
  }, []);

  const editDoc = useCallback(() => {
    navigate(`${PATHS.MONEY_COLLECTION}/${id}`);
  }, [id, navigate]);

  const getLoading = useCallback(() => {
    if (isLoading || isLoadingDelete) {
      return <Loading backgroundFull />;
    }

    return null;
  }, [isLoading, isLoadingDelete]);

  const getLoadingEmail = useCallback(() => {
    if (isLoadingSendEmail) {
      return <Loading />;
    }

    return null;
  }, [isLoadingSendEmail]);

  const deleteConfirmation = useCallback(() => {
    presentAlert({
      mode: 'ios',
      header: t('FORMS.CONFIRM_DELETE_MSG'),
      buttons: [
        {
          text: t('SHARED.CANCEL'),
          role: 'cancel',
          cssClass: 'noTextTransformation',
        },
        {
          text: t('SHARED.CONFIRM'),
          role: 'confirm',
          handler: () => {
            // @ts-expect-error mutation
            deleteEntity({ id });
          },
          cssClass: 'noTextTransformation',
        },
      ],
    });
  }, [presentAlert, t, deleteEntity, id]);

  const getDeleteButton = useCallback(() => {
    if (data?.canDelete) {
      return (
        <IonButton mode="md" onClick={deleteConfirmation} fill="outline" className="ion-delete-button padding-r-4">
          {t('FORMS.DELETE')}
        </IonButton>
      );
    }
  }, [data?.canDelete, deleteConfirmation, t]);

  const downloadDoc = useCallback(() => {
    if (data?.id && data.company) {
      const fileName = `billease-${data?.customer?.companyName}-${data.date}.pdf`;

      return (
        <PDFDownloadLink
          document={<MoneyCollectionPdf doc={data} logo={logoData} currency={currencySymbol} />}
          fileName={fileName}
          style={pdfStyles.ionButton}>
          {t('FORMS.DOWNLOAD_PDF')}
        </PDFDownloadLink>
      );
    }
  }, [data, logoData, currencySymbol, t]);

  const shareDocPublicly = useCallback(() => {
    if (id) {
      publicShareDoc({ entityId: id });

      const publicUrl = `${ENV.DOMAIN}share/money-collection/${id}`;
      navigator?.clipboard?.writeText(publicUrl);
      dispatch(
        setToast({ value: { show: true, msg: t('SHARED.COPIED_TO_CLIPBOARD'), isError: false, position: 'top' } }),
      );
    }
  }, [dispatch, id, publicShareDoc, t]);

  const getShareDocButton = useCallback(
    () => (
      <IonButton mode="md" onClick={shareDocPublicly} data-testid="share-button">
        {t('SHARED.SHARE')}
        <IonIcon slot="end" icon={shareSocialOutline}></IonIcon>
      </IonButton>
    ),
    [t, shareDocPublicly],
  );

  const sendEmailToCustomer = useCallback(
    (email: string) => {
      if (id) {
        sendEmail({ entityId: id, email });
      }
    },
    [id, sendEmail],
  );

  const canSendEmail = useCallback((emailSentAt: string | null | undefined) => {
    if (!emailSentAt) return true;

    return isAfter24Hours(emailSentAt);
  }, []);

  const getSendEmailButton = useCallback(() => {
    if (canSendEmail(data?.emailSentToCustomerAt)) {
      return (
        <IonButton mode="md" onClick={openSendEmail}>
          {t('SHARED.SEND_EMAIL')}
          <IonIcon slot="end" icon={sendSharp}></IonIcon>
        </IonButton>
      );
    }
  }, [data?.emailSentToCustomerAt, canSendEmail, openSendEmail, t]);

  const getActionButtons = useCallback(() => {
    return (
      <div className="padding-b-10 flex-space-between-start-row-wrap width-100">
        <div className="flex-start-center-row-wrap">
          <IonButton mode="md" onClick={editDoc}>
            {t('FORMS.EDIT')}
          </IonButton>
          {downloadDoc()}
          {getShareDocButton()}
        </div>
        <div>
          {getDeleteButton()}
          {getSendEmailButton()}
        </div>
      </div>
    );
  }, [downloadDoc, editDoc, getDeleteButton, getSendEmailButton, getShareDocButton, t]);

  const getEmailPill = useCallback(() => {
    if (data?.emailSentToCustomerAt) {
      return <EmailPill size={16} top={-26} date={data?.emailSentToCustomerAt} />;
    }
  }, [data?.emailSentToCustomerAt]);

  const getPaymentDetails = useCallback(() => {
    if (data?.paycheckBank) {
      return (
        <>
          <ViewField label={t('FORMS.PAY_CHECK_BANK')} value={data?.paycheckBank || ''} />
          <ViewField label={t('FORMS.PAY_CHECK_NUMBER')} value={data?.paycheckNumber || ''} />
          <ViewField label={t('FORMS.PAY_CHECK_DATE')} value={getLocalDate(data?.paycheckDate || '')} />
        </>
      );
    }
  }, [data?.paycheckBank, data?.paycheckDate, data?.paycheckNumber, t]);

  const getMainSection = useCallback(() => {
    if (data && !isLoading) {
      return (
        <div className="docForm margin-t-24">
          {getEmailPill()}
          <div className="padding-t-24 padding-b-24 padding-l-8 padding-r-8">
            <div className="flex-start-center-row-wrap gap-30-16 padding-b-30">
              <ViewField
                label="MONEY_COLLECTION.OTHER_END"
                value={t(`MONEY_COLLECTION.${TRADER_TYPE?.[data.traderType || 1]?.name}`)}
              />
              <ViewField label="FORMS.CUSTOMER" value={data?.customer?.companyName || ''} />
              <ViewField label="MONEY_COLLECTION.DOC_DATE" value={getLocalDate(data?.date)} />
              <ViewField label={t('MONEY_COLLECTION.PRICE', { currency: currencySymbol })} value={data?.value} />
              <ViewField
                label={t('FORMS.PAYMENT_TYPE')}
                value={t(`MONEY_COLLECTION_PAYMENT_TYPES.${data?.paymentType}`)}
              />
              {getPaymentDetails()}
            </div>
            <ViewTextAreaField label={t('FORMS.PUBLIC_NOTES_2')} value={data.notes || ''} minHeight={150} />
          </div>
        </div>
      );
    }
  }, [currencySymbol, data, getEmailPill, getPaymentDetails, isLoading, t]);

  // const getPdf = useCallback(() => {
  //   if (data?.id && data.company) {
  //     return (
  //       <PDFViewer style={{ width: '100%', height: '1300px' }} showToolbar={true}>
  //         <MoneyCollectionPdf doc={data} logo={logoData} currency={currencySymbol} />
  //       </PDFViewer>
  //     );
  //   }
  // }, [data, logoData, currencySymbol]);

  return (
    <div className="width-100 flex-center-center-column-nowrap padding-b-60">
      {getLoading()}
      {getLoadingEmail()}
      <div className="page flex-center-start-column-nowrap">
        {getActionButtons()}
        {getMainSection()}
        {/* {getPdf()} */}
      </div>
      <EmailModal
        visible={showEmailModal}
        initialEmail={data?.customer?.email || ''}
        onCancel={handleCancel}
        onSubmit={sendEmailToCustomer}
      />
    </div>
  );
};
